import {generateInitialState} from "@/helpers/generateInitialState";
import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {userInitialState} from "../initialStates";
import {
  activateCode,
  addCredit,
  deleteAccount,
  generateReferCode,
  loadCustomerVouchers,
  loadMedicalInformation,
  loadReferCodeUses,
  loadUser,
  saveNotificationPreference,
  saveThemePreference,
  updateCustomerProfile,
  updateHealthCategoriesInterest,
} from "./userActions";

const initialState = await generateInitialState("user", userInitialState);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateMedicalInformation: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    globallyClearErrors: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
    },
    updateUserDetails: (state, action) => {
      const newState = {...state.data, ...action.payload};
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = newState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveThemePreference.fulfilled, (state, action) => {
        state.data = {...state.data, ...action.payload};
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(generateReferCode.fulfilled, (state, action) => {
        state.data = {...state.data, referCode: action.payload.voucherCode};
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadReferCodeUses.fulfilled, (state, action) => {
        state.data = {...state.data, referCodeUsage: action.payload};
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(updateCustomerProfile.fulfilled, (state, action) => {
        const newCustomerDetails = action.payload.userUpdated || action.payload.customerDetails || {};
        state.data = {...state.data, ...newCustomerDetails};
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(updateHealthCategoriesInterest.fulfilled, (state, action) => {
        state.data = {
          ...state.data,
          healthCategoriesPreferences: action.payload?.customerHealthCategoriesInterest || [],
        };
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(loadCustomerVouchers.fulfilled, (state, action) => {
        state.vouchers = [...action.payload];
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addMatcher(isAnyOf(loadUser.fulfilled, loadMedicalInformation.fulfilled), (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data = {...state.data, ...action.payload};
      })
      .addMatcher(
        isAnyOf(
          saveNotificationPreference.fulfilled,
          addCredit.fulfilled,
          deleteAccount.fulfilled,
          activateCode.fulfilled,
        ),
        (state, action) => {
          state.isFetching = false;
          state.hasFailed = false;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(
          saveThemePreference.pending,
          generateReferCode.pending,
          loadReferCodeUses.pending,
          updateCustomerProfile.pending,
          updateHealthCategoriesInterest.pending,
          loadCustomerVouchers.pending,
          loadUser.pending,
          loadMedicalInformation.pending,
          saveNotificationPreference.pending,
          addCredit.pending,
          deleteAccount.pending,
          activateCode.pending,
        ),
        (state, action) => {
          state.isFetching = true;
          state.hasFailed = false;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(
          saveThemePreference.rejected,
          generateReferCode.rejected,
          loadReferCodeUses.rejected,
          updateCustomerProfile.rejected,
          updateHealthCategoriesInterest.rejected,
          loadCustomerVouchers.rejected,
          loadUser.rejected,
          loadMedicalInformation.rejected,
          saveNotificationPreference.rejected,
          addCredit.rejected,
          deleteAccount.rejected,
          activateCode.rejected,
        ),
        (state, action) => {
          const {message = "Something went wrong"} = action.payload || {};

          state.isFetching = false;
          state.hasFailed = true;
          state.errorMsg = message;
        },
      );
  },
  selectors: {
    userStateSelector: state => state,
    userStatusSelector: state => ({
      isFetching: state?.isFetching || false,
      hasFailed: state?.hasFailed || false,
      errorMsg: state?.errorMsg || "",
    }),
  },
});

export const {globallyClearErrors, updateMedicalInformation, updateUserDetails} = userSlice.actions;
export const {userStateSelector, userStatusSelector, userErrorMessage} = userSlice.selectors;
export default userSlice.reducer;
